import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { resolveFileUrl } from '@/lib/api-client';
import { FileUpload } from '@/components/ui/file-upload';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import OnboardingLayout from '@/components/OnboardingLayout';
import { useSearchParams } from 'react-router-dom';
import { CloudUpload, X } from 'lucide-react';
import { formatPhoneNumber, validatePhoneNumber } from '@/utils/phoneUtils';
import { US_TIMEZONES } from '@/utils/geoData';
import { US_STATES_OPTIONS } from '@/utils/us-states';
import { validateFile, formatFileSize } from '@/utils/fileUtils';
import { toast } from '@/hooks/use-toast';
import {
  useUpdateCompanyProfile,
  useUploadLogo,
  useGetCompanyData,
} from '@/hooks/use-company';
import { useErrorHandler } from '@/hooks/use-error-handler';

// Form validation schema
const formSchema = z.object({
  companyLogo: z.any().optional(),
  companyName: z.string().min(1, 'Company name is required'),
  legalCompanyPhone: z
    .string()
    .min(1, 'Phone number is required')
    .refine(
      (phone) => validatePhoneNumber(phone),
      'Phone number must be a valid 10-digit US number'
    ),
  timezone: z.string().min(1, 'Timezone is required'),
  address: z.string().min(1, 'Address is required'),
  address2: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(1, 'State is required'),
  zipCode: z
    .string()
    .min(5, 'ZIP code must be 5 digits')
    .max(5, 'ZIP code must be 5 digits')
    .regex(/^\d{5}$/, 'ZIP code must contain only numbers'),
});

type FormValues = z.infer<typeof formSchema>;

export default function Step1CompanyDetails() {
  const [searchParams] = useSearchParams();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [existingLogoUrl, setExistingLogoUrl] = useState<string | null>(null);
  const [logoRemoved, setLogoRemoved] = useState(false);
  // Get organization ID from URL parameters
  const organizationId = searchParams.get('orgId');

  // API hooks
  const updateCompanyProfile = useUpdateCompanyProfile();
  const uploadLogo = useUploadLogo();
  const { handleError } = useErrorHandler();

  // Fetch existing company data
  const { data: companyData, isLoading: isLoadingCompany } = useGetCompanyData(
    organizationId,
    'profile'
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      companyName: '',
      legalCompanyPhone: '',
      timezone: '',
      address: '',
      address2: '',
      city: '',
      state: '',
      zipCode: '',
    },
  });

  // Handle missing orgId — show clear guidance
  useEffect(() => {
    if (!organizationId) {
      toast({
        title: 'Missing Organization ID',
        description: 'Please complete the signup process first or return to the signup page.',
        variant: 'warning',
      });
    }
  }, [organizationId]);

  // Prefill form with existing data
  useEffect(() => {
    if (companyData?.profile) {
      const profile = companyData.profile;

      // Set form values with existing data
      form.reset({
        companyName: profile.name || '',
        legalCompanyPhone: profile.phone || '',
        timezone: profile.timezone || '',
        address: profile.address || '',
        address2: profile.address2 || '',
        city: profile.city || '',
        state: profile.state || '',
        zipCode: profile.zipCode || '',
      });

      // Handle existing logo
      if (profile.logoUrl) {
        setExistingLogoUrl(profile.logoUrl);
        setPreviewUrl(resolveFileUrl(profile.logoUrl));
        setLogoRemoved(false);
      } else {
        setExistingLogoUrl(null);
        setPreviewUrl(null);
        setLogoRemoved(false);
      }
    }
  }, [companyData, form]);

  const handleNext = async (data: FormValues) => {
    if (!organizationId) {
      toast({
        title: 'Organization ID Required',
        description: 'Your session is missing the organization identifier. Please return to signup and complete verification.',
        variant: 'destructive',
      });
      return false; // Prevent navigation
    }

    try {
      let logoUrl: string | null | undefined;

      // Logo + profile update are treated as a single logical operation.
      // Any failure (upload or profile) returns false and blocks navigation to the next step.
      // The parent OnboardingLayout already disables the button while either mutation is pending.
      if (selectedFile) {
        // Case 1: User selected a new file (upload new logo)
        const uploadResult = await uploadLogo.mutateAsync({
          companyId: organizationId,
          file: selectedFile,
        });
        logoUrl = uploadResult.logoUrl;
      } else if (logoRemoved) {
        // Case 2: User removed the existing logo
        logoUrl = null; // Explicitly set to null to remove logo
      } else if (existingLogoUrl && !logoRemoved) {
        // Case 3: Keep existing logo (no changes)
        logoUrl = existingLogoUrl;
      } else {
        // Case 4: No logo (new profile or no logo selected)
        logoUrl = null;
      }

      // Prepare the payload for the API
      const payload = {
        name: data.companyName.trim(),
        logoUrl: logoUrl || null, // Send null instead of empty string for removal
        phone: data.legalCompanyPhone,
        timezone: data.timezone,
        address: data.address,
        address2: data.address2 || null,
        city: data.city,
        state: data.state,
        zipCode: data.zipCode,
      };

      // Update company profile
      await updateCompanyProfile.mutateAsync({
        companyId: organizationId,
        data: payload,
      });

      toast({
        title: 'Profile Updated',
        description: 'Company profile has been updated successfully.',
        variant: 'success',
      });
      return true;
    } catch (error: any) {
      // API errors are surfaced via the global error handler / toast.
      // Zod field errors take precedence via FormMessage; we never overwrite them here.
      handleError(error, {
        customMessage: 'Failed to update company profile. Please try again.',
      });
      return false; // Prevent navigation
    }
  };

  // Cleanup preview URL on component unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // File upload handlers
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      const validation = validateFile(file);
      if (validation.isValid) {
        // Clean up previous preview URL if it was a local file
        if (previewUrl && previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(previewUrl);
        }

        setSelectedFile(file);
        setPreviewUrl(URL.createObjectURL(file));
        setLogoRemoved(false); // Reset removed state when new file is selected
        form.setValue('companyLogo', file);
      } else {
        toast({
          title: validation.error,
          variant: 'warning',
        });
      }
    }
  };

  const handleFileChange = (file: File) => {
    const validation = validateFile(file);
    if (validation.isValid) {
      // Clean up previous preview URL if it was a local file
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }

      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setLogoRemoved(false); // Reset removed state when new file is selected
      form.setValue('companyLogo', file);
    } else {
      toast({
        title: validation.error,
        variant: 'warning',
      });
    }
  };

  const removeFile = () => {
    // Clean up the preview URL if it's a local blob URL
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }

    // Reset file-related state
    setSelectedFile(null);
    setPreviewUrl(null);
    form.setValue('companyLogo', undefined);

    // If there was an existing logo, mark it as removed
    if (existingLogoUrl) {
      setLogoRemoved(true);
    }

    // Reset the file input to allow re-uploading the same file
    const fileInput = document.getElementById(
      'logo-upload'
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  return (
    <OnboardingLayout
      currentStep={1}
      totalSteps={5}
      onNext={async () => {
        const isValid = await form.trigger();
        if (!isValid) return false;

        // Wrap the form submission to ensure it always returns a boolean
        try {
          // Create a promise that resolves to the result of handleNext
          return new Promise((resolve) => {
            form.handleSubmit(async (data) => {
              const result = await handleNext(data);
              resolve(result !== false); // If handleNext returns false, return false; otherwise true
            })();
          });
        } catch (error) {
          console.error('Form submission error:', error);
          return false;
        }
      }}
      isLoading={updateCompanyProfile.isPending || uploadLogo.isPending}
    >
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-foreground mb-2">
            Company Profile Setup
          </h2>
          <p className="text-muted-foreground">
            Enter your business details to help us set up your account correctly
            and ensure compliance with regulations.
          </p>
        </div>

        <Form {...form}>
          <div className="space-y-6">
            {/* Show loading state while fetching company data */}
            {isLoadingCompany && (
              <div className="flex items-center justify-center py-4">
                <div className="w-6 h-6 border-2 border-outline-variant border-t-slate-600 rounded-full animate-spin" />
                <span className="ml-2 text-muted-foreground">
                  Loading existing data...
                </span>
              </div>
            )}
            {/* Legal Company Name */}
            <FormField
              control={form.control}
              name="companyName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground font-medium">
                    Legal Company Name<span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Your legal company name"
                      {...field}
                      className="mt-1"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* Company Logo Upload */}
            <FormField
              control={form.control}
              name="companyLogo"
              render={() => (
                <FormItem>
                  <FormLabel className="text-foreground font-medium">
                    Company Logo (Optional)
                  </FormLabel>
                  <FormControl>
                    <div className="w-full">
                      {!selectedFile && !previewUrl ? (
                        <div
                          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${dragActive
                              ? 'border-border-subtle bg-surface-low'
                              : 'border-border hover:border-border'
                            }`}
                          onDragEnter={handleDrag}
                          onDragLeave={handleDrag}
                          onDragOver={handleDrag}
                          onDrop={handleDrop}
                          onClick={() =>
                            document.getElementById('logo-upload')?.click()
                          }
                        >
                          <CloudUpload className="w-12 h-12 mx-auto text-yellow-500 mb-4" />
                          <p className="text-muted-foreground mb-2">
                            <span className="font-medium">
                              Drag & drop files or Browse
                            </span>
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Supported formats: JPG, PNG, JPEG, SVG
                          </p>
                        </div>
                      ) : (
                        <div className="border-2 border-border rounded-lg p-4">
                          <div className="flex items-start justify-between gap-4">
                            {/* Image Preview */}
                            <div className="flex items-center gap-3">
                              {previewUrl ? (
                                <div className="w-16 h-16 rounded-lg overflow-hidden border border-border/15">
                                  <img
                                    src={previewUrl}
                                    alt="Logo preview"
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              ) : (
                                <div className="w-16 h-16 bg-surface-low rounded-lg flex items-center justify-center">
                                  <CloudUpload className="w-8 h-8 text-yellow-500" />
                                </div>
                              )}
                              <div>
                                {selectedFile ? (
                                  <>
                                    <p className="text-sm font-medium text-foreground">
                                      {selectedFile.name}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      {formatFileSize(selectedFile.size)}
                                    </p>
                                    <p className="text-xs text-blue-600 mt-1">
                                      ✓ New logo selected
                                    </p>
                                  </>
                                ) : existingLogoUrl && !logoRemoved ? (
                                  <>
                                    <p className="text-sm font-medium text-foreground">
                                      Current company logo
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      Stored in cloud
                                    </p>
                                    <p className="text-xs text-green-600 mt-1">
                                      ✓ Existing logo
                                    </p>
                                  </>
                                ) : (
                                  <>
                                    <p className="text-sm font-medium text-foreground">
                                      Logo preview
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      Ready to upload
                                    </p>
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Remove Button */}
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={removeFile}
                              className="text-red-500 hover:text-red-700 flex-shrink-0"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                      <FileUpload
                        id="logo-upload"
                        accept="image/*"
                        onFile={handleFileChange}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* Legal Company Phone and Timezone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="legalCompanyPhone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-foreground font-medium">
                      Legal Company Phone<span className="text-red-600">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="(555) 123-4567"
                        {...field}
                        className="mt-1"
                        onChange={(e) => {
                          const formatted = formatPhoneNumber(e.target.value);
                          field.onChange(formatted);
                        }}
                        maxLength={14}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="timezone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-foreground font-medium">
                      Timezone<span className="text-red-600">*</span>
                    </FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select timezone..." />
                        </SelectTrigger>
                      </FormControl>{' '}
                      <SelectContent>
                        {US_TIMEZONES.map((timezone) => (
                          <SelectItem
                            key={timezone.value}
                            value={timezone.value}
                          >
                            {timezone.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            {/* Address */}
            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground font-medium">
                    Address<span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="123 main street"
                      className="mt-1 min-h-[40px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* Address Line 2 (optional) */}
            <FormField
              control={form.control}
              name="address2"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground font-medium">Address Line 2</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Suite 100 / PO Box 1234"
                      {...field}
                      className="mt-1"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* City, State, ZIP Code */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-foreground font-medium">
                      City<span className="text-red-600">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g., San Francisco"
                        {...field}
                        className="mt-1"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="state"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-foreground font-medium">
                      State<span className="text-red-600">*</span>
                    </FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {US_STATES_OPTIONS.map((state) => (
                          <SelectItem key={state.code} value={state.value}>
                            {state.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="zipCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-foreground font-medium">
                      ZIP Code<span className="text-red-600">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="12345"
                        {...field}
                        className="mt-1"
                        maxLength={5}
                        inputMode="numeric"
                        onChange={(e) => {
                          const numericValue = e.target.value.replace(
                            /\D/g,
                            ''
                          );
                          field.onChange(numericValue);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </Form>
      </div>
    </OnboardingLayout>
  );
}
